import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ShieldCheck } from "lucide-react";
import { NeuCard } from "../../components/neu/Neu";
import { fetchVerifyAttempts } from "../../api/verifyLog";
import { VerifyAttemptRow } from "./VerifyAttemptRow";

const HEADS = ["reports.employee", "check.branch", "verify.at", "check.status", "verify.result", "verify.reason", "verify.liveness", "check.similarity"];

export const VerifyAttempts = ({ filters }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [onlyFailed, setOnlyFailed] = useState(true);

  useEffect(() => {
    fetchVerifyAttempts({ ...filters, only_failed: onlyFailed })
      .then(setData)
      .catch(() => setData(null));
  }, [filters, onlyFailed]);

  if (!data) return null;

  return (
    <NeuCard data-testid="verify-attempts">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="label-xs flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-primary" /> {t("verify.title")}
        </p>
        <label className="flex items-center gap-2 text-xs font-bold" data-testid="verify-only-failed">
          <input type="checkbox" checked={onlyFailed} onChange={(e) => setOnlyFailed(e.target.checked)} />
          {t("verify.onlyFailed")}
        </label>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">{t("verify.hint")}</p>

      <div className="mt-4 flex flex-wrap gap-2" data-testid="verify-totals">
        <span className="rounded-full bg-muted px-3 py-1 text-xs font-bold">
          {t("verify.attempts")}: {data.totals.attempts}
        </span>
        <span className="rounded-full bg-destructive/10 px-3 py-1 text-xs font-bold text-destructive">
          {t("verify.failed")}: {data.totals.failed}
        </span>
        <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary">
          {t("verify.tokens")}: {data.totals.tokens}
        </span>
        {data.totals.reasons.slice(0, 4).map(([reason, count]) => (
          <span key={reason} className="rounded-full bg-amber-500/10 px-3 py-1 text-xs font-bold text-amber-500">
            {t(`verify.short.${reason}`, { defaultValue: reason })}: {count}
          </span>
        ))}
      </div>

      {!data.rows.length ? (
        <p className="mt-4 text-sm text-muted-foreground" data-testid="verify-empty">
          {t("verify.empty")}
        </p>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[820px] text-start text-sm" data-testid="verify-table">
            <thead>
              <tr className="border-b border-border/60">
                {HEADS.map((c) => (
                  <th key={c} className="px-3 py-3 text-start text-[11px] tracking-normal text-muted-foreground">
                    {t(c)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => (
                <VerifyAttemptRow key={row.id} row={row} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </NeuCard>
  );
};
